import React, { ReactNode, useContext } from "react";
import { CreditCard, MapPin, ShieldCheck } from "lucide-react";

import { IntlContext } from "context";
import type { OurServiceInterface } from "types/service";
import { MutedInset } from "components/ui/muted-inset";
import { ProviderDetailCard } from "./ProviderDetailCard";

interface Props {
    provider: OurServiceInterface["provider"];
}

const ProviderInfoRow = ({ icon, children }: { icon: ReactNode; children: ReactNode }) => (
    <div className="flex gap-3">
        <span className="flex h-5 w-5 shrink-0 items-center justify-center text-primary">{icon}</span>
        <div className="min-w-0 flex-1">{children}</div>
    </div>
);

export const ProviderPracticalInfoCard: React.FC<Props> = ({ provider }) => {
    const intl = useContext(IntlContext);

    const paymentMethods = provider.num_cesu
        ? intl.formatMessage("provider.view.payment_card_cesu")
        : intl.formatMessage("provider.view.payment_card_only");

    return (
        <ProviderDetailCard title={intl.formatMessage("provider.view.section_practical_info")}>
            <div className="space-y-4">
                {provider.city && (
                    <ProviderInfoRow icon={<MapPin className="h-4 w-4" />}>
                        <p className="text-sm font-semibold text-foreground">
                            {intl.formatMessage("provider.view.intervention_area")}
                        </p>
                        <MutedInset className="mt-1 ml-[-10px]">
                            <p className="text-sm font-medium text-foreground/80">
                                {provider.city}
                                {provider.postcode && ` (${provider.postcode})`}
                            </p>
                            {provider.travel_range != null && provider.travel_range > 0 && (
                                <p className="mt-0.5 text-xs text-muted-foreground">
                                    {intl.formatMessage("provider.view.intervention_radius", {
                                        count: provider.travel_range,
                                    })}
                                </p>
                            )}
                        </MutedInset>
                    </ProviderInfoRow>
                )}

                <ProviderInfoRow icon={<CreditCard className="h-4 w-4" />}>
                    <p className="text-sm font-semibold text-foreground">
                        {intl.formatMessage("provider.view.payment_methods")}
                    </p>
                    <p className="text-sm text-muted-foreground">{paymentMethods}</p>
                </ProviderInfoRow>

                {provider.dataleon_status === "approved" && (
                    <ProviderInfoRow icon={<ShieldCheck className="h-4 w-4" />}>
                        <p className="text-sm font-semibold text-emerald-600">
                            {intl.formatMessage("provider.card.verified_profile")}
                        </p>
                    </ProviderInfoRow>
                )}
            </div>
        </ProviderDetailCard>
    );
};
